<template>
  <div>
    <!-- 标题 -->
    <h3
      class="font-bold text-[#121212]"
      :class="isPC ? 'mb-[28px] text-[28px]' : 'mb-[32px] text-[36px]'"
    >
      {{ pick($t, 'closePriceTitle', 'closePriceTitlePredict') }}
    </h3>

    <!-- 输入区域 -->
    <div
      class="flex justify-between"
      :class="isPC ? '' : 'h-[172px]'"
    >
      <!-- 左侧：今日收盘价输入 -->
      <div
        class="rounded-[16px] relative"
        :class="isPC ? ' w-[460px]' : 'pl-[24px] pt-[24px]  border border-solid border-[#E4E4E4] w-[472px]'"
      >
        <span
          class="block text-[#999999]"
          :class="isPC ? 'text-[20px] leading-[24px]' : 'text-[24px] leading-[28px]'"
        >
          {{ closeDateLabel }}
        </span>
        <div
          class="flex items-center"
          :class="isPC ? 'mt-[12px] pl-[12px] rounded-[10px] border border-solid border-[#E4E4E4] h-[60px]' : 'mt-[46px]'"
        >
          <span
            class="font-bold text-[#121212]"
            :class="isPC ? 'text-[22px] leading-[36px]' : 'text-[36px] leading-[50px]'"
          >$</span>
          <input
            ref="inputRef"
            v-model="inputValue"
            type="text"
            inputmode="decimal"
            :placeholder="$t('inputPlaceholder')"
            class="ml-[4px] flex-1 min-w-0 border-none outline-none bg-transparent font-bold text-[#121212] placeholder-[#CCCCCC] placeholder-font-normal"
            :class="isPC ? 'text-[22px] leading-[36px]' : 'text-[44px] leading-[50px]'"
            @input="handleInput"
          />
        </div>
        <!-- 与现价对比 badge -->
        <span
          v-if="diffText"
          class="inline-block rounded-[8px] bg-[#F5F6F7] px-[12px] py-[4px] text-[#666666] absolute"
          :class="isPC ? 'text-[18px] leading-[22px] top-[24px] left-[90px]' : 'text-[20px] leading-[36px] top-[52px] left-[140px]'"
        >
          {{ diffText }}
        </span>
      </div>

      <!-- 右侧：现价展示 -->
      <div
        class="shrink-0 rounded-[16px] "
        :class="isPC ? '' : 'w-[208px] pl-[24px] pt-[24px] bg-[#f6f7f9]'"
      >
        <span
          class="block text-[#999999]"
          :class="isPC ? 'text-[20px] leading-[24px]' : 'text-[24px] leading-[28px]'"
        >
          {{ $t('currentPriceLabel') }}
        </span>
        <span
          class="block font-bold text-[#121212]"
          :class="isPC ? 'mt-[12px] text-[22px] leading-[36px] bg-[#f6f7f9] w-[150px] pl-[11px] pt-[15px] h-[60px] rounded-[10px]' : 'mt-[46px] text-[36px] leading-[48px]'"
        >
          ${{ currentPrice }}
        </span>
      </div>
    </div>

    <!-- 提交按钮 -->
    <button
      type="button"
      class="flex mx-auto items-center justify-center rounded-[44px] font-medium text-white outline-none border-none"
      :class="[
        isPC ? 'mt-[48px] w-[360px] h-[50px] text-[20px]' : 'mt-[70px] w-[510px] h-[88px] text-[32px]',
        canSubmit ? 'bg-[#121212]' : 'bg-[#bfc0c2]',
      ]"
      :disabled="!canSubmit"
      @click="handleSubmit"
    >
      {{ $t('closePriceSubmit') }}
    </button>
  </div>
</template>

<script lang="ts">
import { ref, computed, watch } from 'vue-demi';
import { use as fegoUse } from '@fegobull/webapi';
import { useFegoPageInfo } from '@ark/web-shared/use';
import dayjs from 'dayjs';
import { useWording } from '../hooks/useWording';
import locales from '../locales';
const __sfc_main = {};
__sfc_main.props = {
  currentPrice: {
    key: "currentPrice",
    required: true,
    type: String
  },
  defaultValue: {
    key: "defaultValue",
    required: false,
    type: [String, null]
  },
  precision: {
    key: "precision",
    required: false,
    type: Number
  },
  min: {
    key: "min",
    required: false,
    type: Number
  },
  max: {
    key: "max",
    required: false,
    type: Number
  },
  targetDate: {
    key: "targetDate",
    required: false,
    type: [String, null]
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const {
    useI18n
  } = fegoUse;
  const $t = useI18n(locales);
  const {
    isPC
  } = useFegoPageInfo();
  const {
    pick
  } = useWording();
  const closeDateLabel = computed(() => {
    const d = props.targetDate;
    if (!d) return $t('closeLabel');
    const parsed = dayjs(d);
    if (!parsed.isValid()) return $t('closeLabel');
    return parsed.format('M/D') + ' ' + $t('closeLabel');
  });
  const inputRef = ref<HTMLInputElement | null>(null);
  const inputValue = ref('');
  watch(() => props.defaultValue, val => {
    inputValue.value = val || '';
  }, {
    immediate: true
  });
  function handleInput(e: Event) {
    const target = e.target as HTMLInputElement;
    let val = target.value;
    val = val.replace(/[^\d.]/g, '');
    val = val.replace(/^0+(\d)/, '$1');
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }
    if (props.precision != null && parts.length === 2 && parts[1].length > props.precision) {
      val = parts[0] + '.' + parts[1].slice(0, props.precision);
    }
    if (props.max != null && val !== '') {
      const num = parseFloat(val);
      if (!isNaN(num) && num > props.max) {
        val = String(props.max);
      }
    }
    inputValue.value = val;
    target.value = val;
  }
  const numericInput = computed(() => parseFloat(inputValue.value));
  const numericCurrent = computed(() => parseFloat(props.currentPrice));
  const canSubmit = computed(() => {
    if (inputValue.value.length === 0 || isNaN(numericInput.value)) return false;
    // if (props.min != null && numericInput.value < props.min) return false;
    if (props.max != null && numericInput.value > props.max) return false;
    return true;
  });
  const diffText = computed(() => {
    if (!canSubmit.value || isNaN(numericCurrent.value) || numericCurrent.value === 0) return '';
    const diff = (numericInput.value - numericCurrent.value) / numericCurrent.value * 100;
    const absDiff = Math.abs(diff);
    // 差值绝对值 < 0.05% 时 toFixed(1) 会显示为 "0.0%"，不展示
    if (absDiff < 0.05) return '';
    const sign = diff >= 0 ? $t('aboveCurrentPrice') : $t('belowCurrentPrice');
    return sign + ' ' + absDiff.toFixed(1) + '%';
  });
  function normalizeValue(val: string): string {
    return val.replace(/\.$/, '').replace(/^0+(\d)/, '$1');
  }
  function handleSubmit() {
    if (!canSubmit.value) return;
    const normalized = normalizeValue(inputValue.value);
    inputValue.value = normalized;
    emit('submit', normalized);
  }
  return {
    $t,
    isPC,
    pick,
    closeDateLabel,
    inputRef,
    inputValue,
    handleInput,
    canSubmit,
    diffText,
    handleSubmit
  };
};
export default __sfc_main;
</script>
